/*
 *  -----------------------------------------------------------------------
 *    <copyright file="rest-client.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import i18n from 'i18next';
import ky from 'ky';
import { KyOptions } from 'ky/distribution/types/options';
import Settings from '@settings/settings';

export type ValidationErrorResponse = {
    type: string;
    detail: string;
    title: string;
    status: number;
    traceId: string;
    errors?: Record<string, string>[];
};

export type UnprocessableEntityResponse = {
    message: string;
};

const getTokenFromStorage = () => sessionStorage?.getItem('token');

const api = ky.create({
    prefixUrl: Settings.api.restUrl(),
    headers: {
        'Content-Type': 'application/json',
    },
    hooks: {
        beforeRequest: [
            (request) => {
                request.headers.set('Authorization', `Bearer ${getTokenFromStorage()}`);
            },
        ],
        beforeError: [
            (error) => {
                error.message = i18n.t('common:somethingWentWrong');
                return error;
            },
        ],
    },
});

export const getData = <TResponse>(url: string, options?: KyOptions) => api.get(url, options).json<TResponse>();
export const getDataForFile = <TResponse>(url: string, options?: KyOptions) =>
    api.get(url, options).then((result) => {
        return result;
    });

export const postData = <TData, TResponse>(url: string, data: { arg: TData; timeout?: number; signal?: AbortSignal }) =>
    api.post(url, { json: data?.arg, timeout: data?.timeout, signal: data?.signal }).json<TResponse>();

export const postDataForFile = <TData>(url: string, data: { arg: TData; timeout?: number }) => {
    return api
        .post(url, {
            json: data?.arg,
            timeout: data?.timeout,
            headers: { Accept: 'application/*' },
        })
        .then((result) => {
            return result;
        });
};

export const putData = <TData, TResponse>(url: string, data: { arg: TData }) =>
    api.put(url, { json: data?.arg }).json<TResponse>();

export const deleteData = (url: string) => api.delete(url).json();

export const deleteDataWithBody = <TData>(url: string, data: TData) => api.delete(url, { json: data }).json();